import request from '@/utils/live_requests';

const state = {
  status: 'idle',
  subscriptionPlans: [],
  activeSubscriptionPlans: [],
  isFetchingSubscriptionPlans: false,
  isFetchingActiveSubscriptionPlans: false,
};

const getters = {
  fetchStatus: (state) => state.status,
  getSubscriptionPlans: (state) => state.subscriptionPlans,
  getActiveSubscriptionPlans: (state) => state.activeSubscriptionPlans,
  isFetchingSubscriptionPlans: (state) => state.isFetchingSubscriptionPlans,
  isFetchingActiveSubscriptionPlans: (state) => state.isFetchingActiveSubscriptionPlans,
  getSubscriptionPlanById: (state) => (id) => {
    return [...state.subscriptionPlans, ...state.activeSubscriptionPlans].find(plan => plan.id === id);
  },
};

const mutations = {
  // Status mutations
  fetchRequest: (state) => {
    state.status = 'loading';
  },
  fetchSuccess: (state) => {
    state.status = 'success';
  },
  fetchError: (state) => {
    state.status = 'error';
  },
  setStatus: (state, status) => {
    state.status = status;
  },

  // Subscription Plans mutations
  setFetchingSubscriptionPlans: (state, isFetching) => {
    state.isFetchingSubscriptionPlans = isFetching;
  },
  setSubscriptionPlans: (state, subscriptionPlans) => {
    state.subscriptionPlans = subscriptionPlans;
  },

  // Active Subscription Plans mutations
  setFetchingActiveSubscriptionPlans: (state, isFetching) => {
    state.isFetchingActiveSubscriptionPlans = isFetching;
  },
  setActiveSubscriptionPlans: (state, activeSubscriptionPlans) => {
    state.activeSubscriptionPlans = activeSubscriptionPlans;
  },
};

const actions = {
  // Fetch Subscription Plans
  fetchSubscriptionPlans: async ({ commit, rootGetters }) => {
    try {
      commit('setFetchingSubscriptionPlans', true);
      commit('setSubscriptionPlans', []);
      commit('fetchRequest');

      const response = await request(
        'GET',
        'admin/subscription-plans',
        rootGetters['auth/getToken'],
        {},
        {}
      );

      commit('setSubscriptionPlans', response.data.items || response.data);
      commit('fetchSuccess');
    } catch (error) {
      commit('fetchError');
      throw error;
    } finally {
      commit('setFetchingSubscriptionPlans', false);
    }
  },

  // Fetch Active Subscription Plans
  fetchActiveSubscriptionPlans: async ({ commit, rootGetters }) => {
    try {
      commit('setFetchingActiveSubscriptionPlans', true);
      commit('setActiveSubscriptionPlans', []);
      commit('fetchRequest');

      const response = await request(
        'GET',
        'admin/subscription-plans/active',
        rootGetters['auth/getToken'],
        {},
        {}
      );

      commit('setActiveSubscriptionPlans', response.data);
      commit('fetchSuccess');
    } catch (error) {
      commit('fetchError');
      throw error;
    } finally {
      commit('setFetchingActiveSubscriptionPlans', false);
    }
  },

  // Create Subscription Plan
  createSubscriptionPlan: async ({ commit, rootGetters }, payload) => {
    try {
      commit('fetchRequest');

      const response = await request(
        'POST',
        'admin/subscription-plans',
        rootGetters['auth/getToken'],
        payload,
        {}
      );

      commit('fetchSuccess');
      return Promise.resolve(response.data);
    } catch (error) {
      commit('fetchError');
      throw error;
    }
  },

  // Update Subscription Plan
  updateSubscriptionPlan: async ({ commit, rootGetters }, { id, payload }) => {
    try {
      commit('fetchRequest');

      const response = await request(
        'PUT',
        `admin/subscription-plans/${id}`,
        rootGetters['auth/getToken'],
        payload,
        {}
      );

      commit('fetchSuccess');
      return Promise.resolve(response.data);
    } catch (error) {
      commit('fetchError');
      throw error;
    }
  },

  // Delete Subscription Plan
  deleteSubscriptionPlan: async ({ commit, rootGetters }, id) => {
    try {
      commit('fetchRequest');

      const response = await request(
        'DELETE',
        `admin/subscription-plans/${id}`,
        rootGetters['auth/getToken'],
        {},
        {}
      );

      commit('fetchSuccess');
      return Promise.resolve(response.data);
    } catch (error) {
      commit('fetchError');
      throw error;
    }
  },

  // Fetch Subscription Plan by ID
  fetchSubscriptionPlanById: async ({ commit, rootGetters }, id) => {
    try {
      commit('fetchRequest');

      const response = await request(
        'GET',
        `admin/subscription-plans/${id}`,
        rootGetters['auth/getToken'],
        {},
        {}
      );

      commit('fetchSuccess');
      return Promise.resolve(response.data);
    } catch (error) {
      commit('fetchError');
      throw error;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
